import { Box, HStack, Skeleton, useBreakpointValue, VStack } from "@chakra-ui/react-v3";

const skeletonWidths = ["130px", "130px", "110px", "150px", "150px", "150px", "130px", "110px"];

/**
 * Skeleton loader for patient overview content.
 * Shows AI summary card and a table section.
 */
export function ContentLoadingSkeleton() {
  const skeletonCount =
    useBreakpointValue({ base: 3, lg: 4, xl: 6, "2xl": 8 }, { ssr: false }) ?? 3;

  return (
    <Box backgroundColor="bg.canvas">
      <VStack align="stretch" gap={4}>
        {/* Tab buttons row skeleton - matches nav tabs below search */}
        <HStack gap={4} flexWrap="nowrap" width="100%">
          {skeletonWidths.slice(0, skeletonCount).map((width, i) => (
            <Skeleton key={i} height="40px" width={width} borderRadius="md" flexShrink={0} />
          ))}
          {/* "Other" menu button skeleton - shown when tabs overflow */}
          {skeletonCount < skeletonWidths.length && (
            <Skeleton height="40px" width="80px" borderRadius="md" flexShrink={0} />
          )}
        </HStack>

        {/* AI Summary Card Skeleton */}
        <Box
          borderRadius="lg"
          bg="bg.surface"
          px={6}
          py={5}
          border="1px"
          borderColor="border"
          boxShadow="sm"
        >
          <Skeleton height="15px" width="100px" mb={3} />
          <VStack align="stretch" gap={1}>
            <Skeleton height="15px" width="100%" />
            <Skeleton height="15px" width="100%" />
            <Skeleton height="15px" width="95%" />
            <Skeleton height="15px" width="100%" />
            <Skeleton height="15px" width="95%" />
            <Skeleton height="15px" width="60%" />
          </VStack>
        </Box>

        {/* Table Section Skeleton */}
        <Box
          borderRadius="lg"
          bg="bg.surface"
          px={6}
          py={6}
          border="1px"
          borderColor="border"
          boxShadow="sm"
        >
          {/* Table Title + Filter Button */}
          <HStack justify="space-between" mb={6}>
            <Skeleton height="32px" width="120px" />
            <Skeleton height="32px" width="70px" borderRadius="md" />
          </HStack>

          {/* Table Header Row - solid bar without skeleton elements */}
          <Box height="40px" bg="bg.canvas" borderTopRadius="md" />

          {/* Table Rows */}
          <VStack align="stretch" mt={3} gap={0}>
            {[1, 2, 3, 4, 5].map(i => (
              <HStack key={i} gap={4} py={1} px={4} borderBottom="1px" borderColor="border.subtle">
                <Skeleton height="24px" width="40%" />
                <Skeleton height="24px" width="25%" />
                <Skeleton height="24px" width="30%" />
              </HStack>
            ))}
          </VStack>
        </Box>

        {/* Table Section Skeleton */}
        <Box
          borderRadius="lg"
          bg="bg.surface"
          px={6}
          py={6}
          border="1px"
          borderColor="border"
          boxShadow="sm"
        >
          {/* Table Title + Filter Button */}
          <HStack justify="space-between" mb={6}>
            <Skeleton height="32px" width="120px" />
            <Skeleton height="32px" width="70px" borderRadius="md" />
          </HStack>

          {/* Table Header Row - solid bar without skeleton elements */}
          <Box height="40px" bg="bg.canvas" borderTopRadius="md" />

          {/* Table Rows */}
          <VStack align="stretch" mt={3} gap={0}>
            {[1, 2, 3, 4, 5].map(i => (
              <HStack key={i} gap={4} py={1} px={4} borderBottom="1px" borderColor="border.subtle">
                <Skeleton height="24px" width="40%" />
                <Skeleton height="24px" width="25%" />
                <Skeleton height="24px" width="30%" />
              </HStack>
            ))}
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}
